import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { SeasonsApi } from "@/openapi/generated";
import type { CreateSeasonRequest } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export const useSeasonCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (createSeasonRequest: CreateSeasonRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(SeasonsApi, accessToken);

      return service.apiSeasonsPost({
        createSeasonRequest: createSeasonRequest,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Seasons] });
      toast({
        title: "Season created",
        description: "Season created successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create season";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};

